import { useId, type ReactNode } from 'react';

import { cn } from './cn';

/**
 * La marca de XYVONA — Manual de Marca V03 §3.1 y §3.3.
 *
 * Dos trazos diagonales de corte recto que se cruzan en un solo punto. El
 * cruce es la idea: el momento en que una afirmación se encuentra con su
 * prueba.
 *
 * Va inline y no como `<img>`: son ~400 bytes, no cuesta un request, y la
 * versión a un color hereda `currentColor` sin un segundo archivo.
 *
 * ── Dos cosas que este componente NO hace, a propósito
 *
 * **No expone el lockup con eslogan** ("Prove what's real"). No es que esté sin
 * decidir —lo está, ver `docs/DECISIONS.md`— sino que el manual lo reserva para
 * material de primer contacto, "donde la categoría todavía no se conoce". El
 * panel es lo contrario: quien entra acá ya es cliente. La aplicación 02 vive
 * en `web/site/src/components/Marca.astro`, que es la superficie que sí hace
 * primer contacto.
 *
 * **La X nunca es el ícono de verificado** (§3.2). Una X simétrica se lee como
 * cruz, y una cruz se lee como falla en toda convención de estado que existe.
 * El estado verificado usa el escudo con check de `ui/states.tsx`. Si alguna
 * vez alguien pone esta marca dentro de un círculo verde, es un bug.
 */
export function Marca({
  variante = 'horizontal',
  alto = 24,
  plana = false,
  className,
}: {
  /** 01 horizontal (por defecto) · 03 vertical · 04 sólo símbolo */
  variante?: 'horizontal' | 'vertical' | 'simbolo';
  /** Alto del símbolo en px. El mínimo del manual en pantalla es 16. */
  alto?: number;
  /** Un solo color, heredando `currentColor`. */
  plana?: boolean;
  className?: string;
}): ReactNode {
  // El degradado necesita un id único por instancia: dos marcas en la misma
  // página compartiendo id hacen que la segunda pinte con el de la primera.
  const id = useId();
  const relleno = plana ? 'currentColor' : `url(#${id})`;

  return (
    <span
      className={cn(
        'inline-flex items-center',
        variante === 'vertical' ? 'flex-col gap-1' : 'gap-2',
        className,
      )}
    >
      <svg
        viewBox="0 0 64 64"
        style={{ height: `${Math.max(alto, 16)}px` }}
        className="block w-auto flex-none"
        aria-hidden="true"
        focusable="false"
      >
        {!plana && (
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#6C4CFF" />
              <stop offset="1" stopColor="#4B2CCB" />
            </linearGradient>
          </defs>
        )}
        <path d="M6 6 H22 L58 58 H42 Z" fill={relleno} />
        <path d="M42 6 H58 L22 58 H6 Z" fill={relleno} />
        {/* El tono trenzado del cruce representa la superposición. No es un
            tercer color: en reproducción a un color desaparece. */}
        {!plana && <path d="M24 32 L32 20.4 L40 32 L32 43.6 Z" fill="#0E0F14" opacity=".22" />}
      </svg>

      {variante !== 'simbolo' && (
        /* El logotipo es la única excepción a la composición del sistema: caja
           alta, interletrado amplio. Nunca en caja alta y baja, nunca
           abreviado a "XYV". El margen negativo compensa el hueco que el
           interletrado deja después de la última letra. */
        <span className="font-display -me-[0.18em] font-bold tracking-[0.18em] text-ink uppercase">
          XYVONA
        </span>
      )}
    </span>
  );
}
